<!-- 富文本编辑器模拟输入框组件 -->
<template>
  <div class="editor-input-wrapper">
    <div
      ref="editor"
      class="editorInput"
      contenteditable="true"
      @focus="handleFocus"
      @blur="handleBlur"
      @paste="handlePaste"
      @keydown.enter="handleEnter"
      @input="handleInput"
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragenter="handleDragEnter"
      @click="saveSelection"
      @keyup="saveSelection"
      @mouseup="saveSelection"
    ></div>
    <transition name="fade">
      <div
        class="rightBox fade-transition"
        v-if="!editorFocused && !hasContent && !isCheck"
      >
        <svg-btn
          v-if="icon"
          class="inputIcon"
          icon-class="icon-input"
        ></svg-btn>
        <span class="text">{{ text }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  props: {
    //是否显示icon图标
    icon: {
      type: Boolean,
      default: false,
    },
    //模拟placeholder
    text: {
      type: String,
      default: '',
    },
    // 初始内容
    initialContent: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      editorFocused: false,
      hasContent: false, // 跟踪输入框是否有内容
      savedSelection: null, // 新增：用于保存选区
      isCheck: false, //是否在点击表情
    }
  },
  mounted() {
    // 初始化编辑器内容
    if (this.initialContent) {
      this.$refs.editor.innerHTML = this.initialContent
      this.checkContent()
    }
  },
  methods: {
    // 清除编辑器内容方法
    clearContent() {
      const editor = this.$refs.editor
      // 清空编辑器内容
      editor.innerHTML = ''
      // 更新内容状态
      this.hasContent = false
      // 触发输入事件，通知父组件内容已清空
      const inputEvent = new Event('input', { bubbles: true })
      editor.dispatchEvent(inputEvent)
      this.$emit('input', '')
    },
    // 保存选区方法
    saveSelection() {
      if (window.getSelection) {
        const selection = window.getSelection()
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          this.savedSelection = range.cloneRange()
        }
      }
    },

    // 恢复选区方法
    restoreSelection() {
      if (this.savedSelection && window.getSelection) {
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(this.savedSelection)
        // 恢复后清除保存的选区，避免重复使用
        this.savedSelection = null
      }
    },

    // 检查选区是否在编辑器内
    selectionInEditor(selection) {
      if (selection.rangeCount === 0) return false

      const range = selection.getRangeAt(0)
      const editorNode = this.$refs.editor

      // 检查选区是否包含在编辑器内
      return editorNode.contains(range.commonAncestorContainer)
    },

    // 插入表情方法
    insertEmoji(emojiUrl) {
      const editor = this.$refs.editor
      // editor.focus() // 确保编辑器获得焦点
      // 恢复之前保存的选区
      this.restoreSelection()

      const img = document.createElement('img')
      img.src = emojiUrl
      img.classList.add('emoji-img')
      img.setAttribute('data-emoji', 'true') // 添加标识属性
      //给img添加点击事件，用于移动光标到后面
      img.addEventListener('click', this.moveCursorAfterImg) // 绑定事件
      const selection = window.getSelection()

      // 关键修复：确保操作只在编辑器内进行
      if (selection.rangeCount > 0 && this.selectionInEditor(selection)) {
        const range = selection.getRangeAt(0)
        range.deleteContents()
        range.insertNode(img)
        // 移动光标到表情后
        const newRange = document.createRange()
        newRange.setStartAfter(img)
        newRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(newRange)
      } else {
        // 没有有效选择时插入到末尾
        editor.appendChild(img)
        // 移动光标到编辑器末尾
        const range = document.createRange()
        range.selectNodeContents(editor)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      }
      // 保存新的选区位置
      this.saveSelection()
      editor.blur()
      // 触发输入事件
      const event = new Event('input', { bubbles: true })
      editor.dispatchEvent(event)

      // 通知父组件内容已更新
      this.$emit('input', editor.innerHTML)
      this.checkContent()
    },
    moveCursorAfterImg(event) {
      // 阻止事件冒泡（避免触发编辑器其他点击事件）
      event.stopPropagation()

      const img = event.target // 获取当前点击的img元素
      const selection = window.getSelection()
      const range = document.createRange() // 创建范围对象

      // 将范围起始位置设置在img之后
      range.setStartAfter(img)
      // 折叠范围（光标在起始位置，即img后面）
      range.collapse(true)

      // 清除现有选区，设置新选区
      selection.removeAllRanges()
      selection.addRange(range)

      // 确保编辑器获得焦点（可选，根据需求）
      this.$refs.editor.focus()
    },
    // 检查内容是否存在
    checkContent() {
      const content = this.$refs.editor.innerHTML.trim()
      this.hasContent = content !== ''
    },

    // 处理输入事件
    handleInput(event) {
      if (this.$refs.editor.innerHTML == '<br>') {
        // 可以选择清空编辑器或保持现状
        this.$refs.editor.innerHTML = '' // 完全清空
      }
      this.$emit('input', this.$refs.editor.innerHTML)
      this.checkContent()
    },

    // 处理获得焦点事件
    handleFocus(event) {
      this.editorFocused = true
      // this.$emit('focus', event)
    },

    // 处理失去焦点事件 - 修改：在失去焦点前保存选区
    handleBlur(event) {
      this.saveSelection() // 在失去焦点前保存选区
      this.editorFocused = false
      // this.$emit('blur', event)
      this.checkContent()
    },

    // 处理粘贴事件
    handlePaste(event) {
      // 阻止默认粘贴行为
      event.preventDefault()

      // 获取粘贴的文本内容
      const text = (event.originalEvent || event).clipboardData.getData(
        'text/plain'
      )

      // 插入纯文本
      document.execCommand('insertText', false, text)

      // 触发输入事件
      const inputEvent = new Event('input', { bubbles: true })
      this.$refs.editor.dispatchEvent(inputEvent)
      this.checkContent()
    },

    // 处理回车事件
    handleEnter(event) {
      // 阻止默认行为
      event.preventDefault()

      // 插入换行
      document.execCommand('insertLineBreak')

      // 触发输入事件
      const inputEvent = new Event('input', { bubbles: true })
      this.$refs.editor.dispatchEvent(inputEvent)

      // 通知父组件按下了回车
      // this.$emit('enter', event)
      this.checkContent()
    },

    // 处理拖放事件
    handleDrop(event) {
      event.preventDefault()
      // this.$emit('drop', event)
      this.checkContent()
    },

    // 处理拖拽经过事件
    handleDragOver(event) {
      event.preventDefault()
      // this.$emit('dragover', event)
      this.checkContent()
    },

    // 处理拖拽进入事件
    handleDragEnter(event) {
      event.preventDefault()
      // this.$emit('dragenter', event)
      this.checkContent()
    },
  },
}
</script>

<style lang="scss" scoped>
.editor-input-wrapper {
  position: relative;
  width: 100%;
  .editorInput {
    // margin-top: 4px;
    min-height: 40px;
    /* 设置最小高度 */
    max-height: 106px;
    border-radius: 20px;
    padding: 9px 16px 9px 16px !important;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    border: 1px solid transparent;
    white-space: pre-wrap;
    word-break: break-all;
    overflow-y: auto !important;
    @include useTheme {
      color: getVar('text-color1-2c2c2c');
      caret-color: getVar('primary-color');
      background: getVar('bg-color14-f4f6f8');
    }

    &:hover {
      @include useTheme {
        border: 1px solid getVar('primary-color');
      }
    }

    &:focus {
      @include useTheme {
        // outline: 2px solid getVar('primary-color4');
        // border: 1px solid getVar('primary-color');
        // background: getVar('bg-color-content');
        border: 1px solid transparent !important;
        outline: none !important;
      }
    }
  }
  .rightBox {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    display: flex;
    align-items: center;

    .inputIcon {
      width: 20px;
      height: 20px;
      margin-right: 8px;

      @include useTheme {
        color: getVar('text-color6-b8bec4');
      }
    }

    .text {
      font-weight: 400;
      font-size: 14px;
      line-height: 22px;
      font-family: PingFangSC, PingFang SC;
      padding-right: 16px;
      @include multiline(1);

      @include useTheme {
        color: getVar('text-color6-b8bec4');
      }
    }
  }
  /* 新增动画和过渡样式 */
  @keyframes fadeOutLeft {
    from {
      opacity: 1;
      transform: translate(0, -50%);
    }
    to {
      opacity: 0;
      transform: translate(-10px, -50%);
    }
  }

  @keyframes fadeInLeft {
    from {
      opacity: 0;
      transform: translate(-10px, -50%);
    }
    to {
      opacity: 1;
      transform: translate(0, -50%);
    }
  }

  .fade-transition {
    transition: opacity 0.3s, transform 0.3s;
  }

  .fade-enter-active {
    animation: fadeInLeft 0.3s forwards;
  }
  .fade-leave-active {
    animation: fadeOutLeft 0.3s forwards;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
